import { Button, Descriptions, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
// eslint-disable-next-line max-len
import { type AudiCalculationModuleSettingDataFragment } from '../../../../../api/fragments/AudiCalculationModuleSettingData';
import { type AudiCalculationModuleSpecsFragment } from '../../../../../api/fragments/AudiCalculationModuleSpecs';
import ConsolePageWithHeader from '../../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { HeaderTabs } from '../types';
import AudiCalculationSettingTabFinanceProduct from './AudiCalculationSettingTabFinanceProduct';
import AudiCalculationSettingTabInsuranceProduct from './AudiCalculationSettingTabInsuranceProduct';
import AudiCalculationSettingTabMainDetails from './AudiCalculationSettingTabMainDetails';

type UpdateAudiCalculationInnerTabProps = {
    activeTab: HeaderTabs;
    module: AudiCalculationModuleSpecsFragment;
    setting: AudiCalculationModuleSettingDataFragment;
};

const UpdateAudiCalculationInnerTab = ({ activeTab, module, setting }: UpdateAudiCalculationInnerTabProps) => {
    switch (activeTab) {
        case HeaderTabs.MainDetails:
            return <AudiCalculationSettingTabMainDetails module={module} setting={setting} />;

        case HeaderTabs.FinanceProducts:
            return <AudiCalculationSettingTabFinanceProduct module={module} setting={setting} />;

        case HeaderTabs.InsuranceProducts:
            return <AudiCalculationSettingTabInsuranceProduct module={module} setting={setting} />;

        default:
            return null;
    }
};

export type UpdateAudiCalculationSettingInnerPageProps = {
    module: AudiCalculationModuleSpecsFragment;
    setting: AudiCalculationModuleSettingDataFragment;
};

const UpdateAudiCalculationSettingInnerPage = ({ module, setting }: UpdateAudiCalculationSettingInnerPageProps) => {
    const { t } = useTranslation(['audiCalculationModuleDetails']);

    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('audiCalculationModuleDetails:descriptions.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('audiCalculationModuleDetails:descriptions.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('audiCalculationModuleDetails:descriptions.financingModule')}>
                {setting.bankModule.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('audiCalculationModuleDetails:descriptions.vehicleModule')}>
                {setting.vehicleModules.map(vehicleModule => vehicleModule.displayName).join(', ')}
            </Descriptions.Item>
            {setting.insuranceModule && (
                <Descriptions.Item label={t('audiCalculationModuleDetails:descriptions.insuranceModule')}>
                    {setting.insuranceModule.displayName}
                </Descriptions.Item>
            )}
        </Descriptions>
    );

    const availableTabs = useMemo(
        () => [
            {
                label: t('audiCalculationModuleDetails:tabs.mainDetails.title'),
                key: HeaderTabs.MainDetails,
                show: true,
            },
            {
                label: t('audiCalculationModuleDetails:tabs.financeProducts.title'),
                key: HeaderTabs.FinanceProducts,
                show: true,
            },
            {
                label: t('audiCalculationModuleDetails:tabs.insuranceProducts.title'),
                key: HeaderTabs.InsuranceProducts,
                show: setting.insuranceModule !== null,
            },
        ],
        [setting.insuranceModule, t]
    );

    return (
        <ConsolePageWithHeader
            content={content}
            footer={
                activeTab === HeaderTabs.MainDetails && [
                    <Button form="updateMainDetails" htmlType="submit" type="primary">
                        {t('audiCalculationModuleDetails:actions.update')}
                    </Button>,
                ]
            }
            header={{
                footer: (
                    <Tabs
                        activeKey={activeTab}
                        items={availableTabs.filter(tabs => tabs.show)}
                        onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                    />
                ),
            }}
            onBack={() => navigate(`/admin/system/modules/${module.id}`)}
            title={t('audiCalculationModuleDetails:settings.title')}
        >
            <UpdateAudiCalculationInnerTab activeTab={activeTab} module={module} setting={setting} />
        </ConsolePageWithHeader>
    );
};

export default UpdateAudiCalculationSettingInnerPage;
